import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import equals from 'ramda/src/equals';
import clone from 'ramda/src/clone';
import uniq from 'ramda/src/uniq';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import filter from 'ramda/src/filter';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Pagination from '@components/@core/Pagination';
import Thumbnail from '@components/@core/Thumbnail';
import Toggle from '@components/@core/Toggle';
import Upload from '@components/@core/Upload';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { LearningModule, LibraryCardEditMenu, TimeIntervals, InitQuiz } from '~/services/config';
import Question from './Question';

import QuillEditor from '../@core/QuillEditor';

const Edit = ({ data, onCardMenu }) => {
    const dispatch = useDispatch();

    const role = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const categories = useSelector((state) => state.app.categories);
    const assessments = useSelector((state) => state.develop.templates.assessments.data?.quiz?.items);

    const [nameEn, setNameEn] = useState(data?.data?.en?.name || '');
    const [nameEs, setNameEs] = useState(data?.data?.es?.name || '');
    const [descriptionEn, setDescriptionEn] = useState(data.data.en?.description || '');
    const [descriptionEs, setDescriptionEs] = useState(data.data.es?.description || '');
    const [type, setType] = useState(find(propEq('id', data.card_type_id), LearningModule));
    const [selected, setSelected] = useState({
        author: [data.author_id],
        department: data.data.department || [],
        competency: data.data.competency || [],
        category: data.data.category || [],
    });

    const [thumbnail, setThumbnail] = useState(data.data.thumb_url);
    const [videoUrl, setVideoUrl] = useState(data.data.video_url);
    const [linkEn, setLinkEn] = useState(data.data.en?.link || data.data.linkEn || data.data.link || '');
    const [linkEs, setLinkEs] = useState(data.data.es?.link || data.data.linkEs || '');
    const [objectives, setObjectives] = useState(data.data.objectives || []);
    const [points, setPoints] = useState(data.data.completion_points || data.data.completion_points || null);
    const [attachments, setAttachments] = useState(data.data.attachments || []);

    const [quiz, setQuiz] = useState([
        {
            wording: '',
            rules: [
                { answer: '', options: false },
                { answer: '', options: false },
            ],
        },
    ]);
    const [questions, setQuestions] = useState([]);

    const [approval, setApproval] = useState(data.data.action_approval === 'manager');
    const [video, setVideo] = useState(data?.data?.video);
    const [file, setFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [page, setPage] = useState(1);
    const [per, setPer] = useState(1);

    const extractHTMLContent = (string) => {
        const span = document.createElement('span');
        span.innerHTML = string;
        return span.textContent || span.innerText;
    };

    const getQuizQuestions = (e) => {
        if (!e) return [];
        const quizs = Object.values(e);
        const quizQuestions = quizs.map((item) => {
            const _item = clone(item);
            if (item?.rules?.options) {
                const options = Object.values(item.rules.options);
                _item.rules.options = options;
            }
            if (item?.rules?.answer) {
                const _answer = filter((e) => e !== '', item.rules.answer);
                _item.rules.answer = _answer;
            }
            _item.type = Number(item.type);
            return _item;
        });
        return quizQuestions;
    };

    const disabled =
        equals(nameEn, data?.data?.en?.name || '') &&
        equals(nameEs, data?.data?.es?.name || '') &&
        equals(extractHTMLContent(descriptionEn), extractHTMLContent(data.data.en?.description || '')) &&
        equals(extractHTMLContent(descriptionEs), extractHTMLContent(data.data.es?.description || '')) &&
        equals(questions, getQuizQuestions(assessments)) &&
        equals(selected.author, [data.author_id]) &&
        equals(selected.department, data.data.department) &&
        equals(selected.competency, data.data.competency) &&
        equals(selected.category, data.data.category) &&
        equals(objectives, data.data.objectives || []) &&
        equals(points, data.data.completion_points || data.data.completion_points || null) &&
        equals(thumbnail, data.data.thumb_url) &&
        equals(attachments, data.data.attachments || []) &&
        equals(approval, data.data.action_approval === 'manager') &&
        equals(linkEn, data.data.en?.link || data.data.linkEn || data.data.link || '') &&
        equals(linkEs, data.data.es?.link || data.data.linkEs || '') &&
        equals(videoUrl, data.data.video_url) &&
        equals(videoFile, null) &&
        equals(file, null);

    const handleObjectiveChange = (index) => (e) => {
        const _objectives = clone(objectives);
        _objectives[index] = e;
        setObjectives(_objectives);
    };
    const handleObjectiveAdd = () => {
        const _objectives = clone(objectives);
        _objectives.push('');
        setObjectives(_objectives);
    };
    const handleObjectiveDelete = (index) => () => {
        const _objectives = clone(objectives);
        _objectives.splice(index, 1);
        setObjectives(_objectives);
    };
    const handleAddAnswer = (e) => {
        const _questions = clone(questions);
        _questions[e - 1].rules.options.push('');
        setQuestions(_questions);
    };
    const handleAddQuestion = () => {
        const _questions = clone(questions);
        _questions.push(InitQuiz[1]);
        setQuestions(_questions);
    };
    const handleChangeQuestion = (data, idx) => {
        const _questions = clone(questions);
        _questions[idx].wording = data;
        setQuestions(_questions);
    };
    const handleChangeAnswers = (data, idx1, idx2) => {
        const _questions = clone(questions);
        if (_questions[idx1].type === 3 || _questions[idx1].type === 4) _questions[idx1].rules.options[idx2] = data;
        else _questions[idx1].rules.answer = data;
        setQuestions(_questions);
    };
    const handleChangeChecked = (e, idx1, idx2) => {
        const _questions = clone(questions);
        const index = `${idx2}`;
        if (questions[idx1].type === 3) {
            _questions[idx1].rules.answer = e ? index : '';
        } else {
            let { answer } = _questions[idx1].rules;
            if (e) answer.push(index);
            else answer = filter((x) => x != index, answer);
            _questions[idx1].rules.answer = uniq(answer);
        }
        setQuestions(_questions);
    };
    const handleSelectTags = (key) => (tags) => {
        const _selected = clone(selected);
        _selected[key] = tags;
        setSelected(_selected);
    };
    const handleChangeQuizType = (e, idx) => {
        const _questions = clone(questions);
        _questions[idx] = InitQuiz[e[0]];
        setQuestions(_questions);
    };
    const handleRemoveQuestion = (idx) => {
        if (questions.length === 1) {
            toast.error('Quiz module should have one more questions.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        const _questions = clone(questions);
        _questions.splice(idx, 1);
        setQuestions(_questions);
    };
    const handlePreview = () => {
        if (data.data.presentation_url && !data.data.presentation_images) {
            toast.warn('This file is being converted to a presentation.  This process takes a few short minutes.', {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        const _data = clone(data);
        _data.data.en = { ..._data.data.en, name: nameEn, description: descriptionEn, link: linkEn };
        _data.data.es = { ..._data.data.es, name: nameEs, description: descriptionEs, link: linkEs };
        _data.data.objectives = objectives;
        _data.data.thumb_url = thumbnail;
        _data.data.video = video;
        delete _data.data.link;
        delete _data.data.linkEn;
        delete _data.data.linkEs;
        dispatch(
            AppActions.modalRequest({
                type: 'Preview',
                data: { before: { course: { children: [_data] }, module: _data, index: 0 } },
            })
        );
    };
    const handleFile = (e) => {
        e.length > 1 ? setFile(e) : setFile(e[0]);
    };

    const handleVideoFile = (e) => {
        e.length > 1 ? setVideoFile(e) : setVideoFile(e[0]);
    };
    const handleRemove = (e) => {
        if (e < attachments.length) {
            const files = clone(attachments);
            files.splice(e, 1);
            setAttachments(files);
        }
    };
    const handleDocumentName = (document) => {
        let filename = document?.split('%2F');
        if (filename.length > 1) {
            filename.splice(0, 2);
            return filename;
        } else {
            return document;
        }
    };
    const handleUpdate = (published) => () => {
        if (nameEn === '' || extractHTMLContent(descriptionEn) === '' || selected.author === '') {
            toast.error('Please enter Title, Description and Author properly to publish a draft.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        
        if (nameEn.length <= 5) {
            toast.error('Title must be more than 5 characters.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        
        if (extractHTMLContent(descriptionEn).length <= 10) {
            toast.error('Description must be more than 10 characters.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }

        const _data = clone(data);
        const _question = clone(questions);

        _data.author_id = selected.author[0];
        _data.data.en = { ..._data.data.en, name: nameEn, description: descriptionEn, link: linkEn };
        _data.data.es = { ..._data.data.es, name: nameEs, description: descriptionEs, link: linkEs };
        _data.data.attachments = attachments;
        _data.data.description = descriptionEn;
        _data.data.department = selected.department;
        _data.data.competency = selected.competency;
        _data.data.category = selected.category;
        _data.data.video = video;
        _data.data.action_approval = approval ? 'manager' : 'self';
        _data.data.objectives = objectives.filter((e) => !!e);
        _data.data.completion_points = points;
        if (!(thumbnail instanceof File)) _data.data.thumb_url = thumbnail;
        delete _data.data.link;
        delete _data.data.linkEn;
        delete _data.data.linkEs;
        if (!(videoUrl instanceof File)) _data.data.video_url = videoUrl;

        const { name: _legacyName, ...templateData } = _data;
        let payload = { template: { ...templateData, published } };

        const items = Object.assign(..._question.map((x, index) => ({ [index + 1]: x })));

        if (type.label === 'Quiz') {
            payload = {
                assessment: {
                    id: data.data.assessment_id,
                    company_id: 0,
                    user_id: null,
                    title: nameEn,
                    type: 2,
                    status: 0,
                    author_id: selected.author[0],
                    data: {
                        quiz: {
                            items,
                            rules: { taken: `${questions.length}`, correct: `${questions.length}` },
                        },
                        settings: { order: 'linear', show_correct: 'yes' },
                        en: { name: nameEn, description: descriptionEn, link: linkEn },
                        es: { name: nameEs, description: descriptionEs, link: linkEs },
                        category: selected.category,
                        competency: selected.competency,
                        department: selected.department,
                        objectives,
                        assessment_id: data.data.assessment_id,
                    },
                    result: null,
                    deleted_at: null,
                    published,
                },
                data: { ...templateData, published, updated_at: new Date().toISOString() },
            };
        }

        const after = { type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST', target: 'modules' };
        dispatch(
            DevelopActions.librarytemplatesaveRequest(
                payload,
                [thumbnail === data.data.thumb_url ? null : thumbnail, videoUrl === data.data.video_url ? null : videoUrl, videoFile, file],
                'modules',
                after,
                '/library/training?active=modules'
            )
        );
    };

    useEffect(() => {
        if (assessments) {
            setQuiz(assessments);
        }
    }, [assessments]);

    useEffect(() => {
        setQuestions(getQuizQuestions(quiz));
    }, [quiz]);

    const [focus, setFocus] = useState(false);

    const showAttachment = !['Quiz'].includes(type.label);
    const handleClear = () => {
        setDescriptionEn('');
    };

    return (
        <>
            <Filter back="all modules" />
            <div className="library-module-card border-5">
                <div className="detail-content px-3 px-sm-0">
                    <div className="d-flex jc-between my-3">
                        <span className="dsl-b22 bold">{`Edit ${type.label} Module`}</span>
                        <EditDropdown options={LibraryCardEditMenu[role]} onChange={onCardMenu} />
                    </div>
                    <div className="d-flex ssm-column mt-3">
                        <div>
                            <p className="dsl-m12 mb-1">Module image</p>
                            <Thumbnail source={thumbnail} type="upload" size="medium" label="Upload image" isDisablePicker onDrop={(e) => setThumbnail(e)} onDelete={() => setThumbnail(null)} />
                        </div>
                        <div className="d-flex flex-column ps-4 ssp-l-0 mt-0 ssm-t-2 d-flex-1">
                            <Input className="module-input mb-3" title="Module title" placeholder="Type here..." value={nameEn} direction="vertical" onChange={(e) => setNameEn(e)} />
                            <span className="title-font">Module description</span>
                            <div className="outer-quill">
                                <QuillEditor
                                    className="module-text d-flex-1"
                                    title="Module description"
                                    placeholder="Type here..."
                                    theme="snow"
                                    value={descriptionEn}
                                    modules={{ toolbar: { container: [['link']] } }}
                                    onChange={(e) => setDescriptionEn(e)}
                                    onFocus={() => setFocus(true)}
                                />
                                {extractHTMLContent(descriptionEn) && focus && (
                                    <div className="clear" onClick={handleClear}>
                                        <Icon name="fas fa-xmark-circle cursor-pointer" color="#969faa" size={16} />
                                    </div>
                                )}
                            </div>
                            <Input
                                className="module-input mb-3 mt-3"
                                title="Module title (Spanish)"
                                placeholder="Type here..."
                                value={nameEs}
                                direction="vertical"
                                onChange={(e) => setNameEs(e)}
                            />
                            <span className="title-font">Module description (Spanish)</span>
                            <div className="outer-quill">
                                <QuillEditor
                                    className="module-text d-flex-1"
                                    title="Module description (Spanish)"
                                    placeholder="Type here..."
                                    theme="snow"
                                    value={descriptionEs}
                                    modules={{ toolbar: { container: [['link']] } }}
                                    onChange={(e) => setDescriptionEs(e)}
                                    onFocus={() => setFocus(true)}
                                />
                                {extractHTMLContent(descriptionEs) && focus && (
                                    <div className="clear" onClick={() => setDescriptionEs('')}>
                                        <Icon name="fas fa-xmark-circle cursor-pointer" color="#969faa" size={16} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {type.label === 'Video' && (
                        <div className="d-flex d-ssm-block ai-center mt-3">
                            <div className="d-flex-1">
                                <p className="dsl-m12 mb-1">Video</p>
                                <Upload type="low" accept="video/*" title="Upload video file" icon="fas fa-file-upload" color="#376caf" size={16} onDrop={handleVideoFile} />
                                <div className="px-2">
                                    {videoUrl && (
                                        <p className="dsl-b16 mb-0">
                                            <a className="dsl-b16 text-primary word-break" href={videoUrl} target="_blank">
                                                {handleDocumentName(videoUrl)}
                                            </a>
                                            <Button type="link" onClick={() => setVideoUrl('')}>
                                                <Icon name="fas fa-trash-can" size={14} color="#376caf" />
                                            </Button>
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex-1 mt-2">
                                <p className="dsl-m12 mb-1">Or place the video link</p>
                                <Input className="video-url" placeholder="Type here..." direction="vertical" value={video} onChange={(e) => setVideoUrl(e)} />
                            </div>
                        </div>
                    )}

                    <div className="d-flex mt-3">
                        {showAttachment && (
                            <div className="d-flex-1">
                                <p className="dsl-m12 mb-1">File</p>
                                <Upload
                                    title="Upload file"
                                    icon="fas fa-file-upload"
                                    color="#376caf"
                                    accept=".ppt, .pptx, .pot, .potx, .odp, .pdf, .xls, .xlsx, .doc, .docx"
                                    size={16}
                                    multiple
                                    onDrop={handleFile}
                                />
                                <div className="px-2">
                                    {attachments?.map((attachment, index) => (
                                        <p key={index} className="dsl-b16 mb-0">
                                            <a className="dsl-b16 text-primary word-break" href={attachment} target="_blank">
                                                {handleDocumentName(attachment)}
                                            </a>
                                            <Button type="link" onClick={() => handleRemove(index)}>
                                                <Icon name="fas fa-trash-can" size={14} color="#376caf" />
                                            </Button>
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                        {type.label === 'Scorm' && (
                            <div className="d-flex-1 ms-4">
                                <Input className="video-url" placeholder="Paste English scorm link..." title="Scorm link (English)" direction="vertical" value={linkEn} onChange={(e) => setLinkEn(e)} />
                                <Input className="video-url mt-3" placeholder="Paste Spanish scorm link..." title="Scorm link (Spanish)" direction="vertical" value={linkEs} onChange={(e) => setLinkEs(e)} />
                            </div>
                        )}
                    </div>

                    <p className="dsl-m12 mt-4">Learning objectives:</p>
                    {objectives.map((item, index) => (
                        <div className="d-flex" key={`ob${index}`}>
                            <Input
                                className="module-input mb-2 d-flex-1"
                                key={`objectives${index}`}
                                value={item}
                                placeholder="Type here..."
                                direction="vertical"
                                onChange={handleObjectiveChange(index)}
                            />
                            <Button className="ml-auto" type="link" onClick={handleObjectiveDelete(index)}>
                                <Icon name="fas fa-trash-can" size={14} color="#376caf" />
                            </Button>
                        </div>
                    ))}
                    <Button className="ml-auto" type="link" onClick={handleObjectiveAdd}>
                        <Icon name="fas fa-plus" size={14} color="#376caf" />
                        <span className="dsl-p14 text-400 ms-1">ADD OBJECTIVE</span>
                    </Button>
                    <Input
                        className="module-input mt-3"
                        dataCy="library-training-module-completion_points"
                        type="number"
                        placeholder="Enter points..."
                        title="Completion Points"
                        direction="vertical"
                        value={points}
                        onChange={(e) => setPoints(parseInt(e) || null)}
                    />
                    {type.label === 'Quiz' && (
                        <>
                            {questions.map(
                                (item, index) =>
                                    index < per * page &&
                                    index >= per * (page - 1) && (
                                        <Question
                                            key={index}
                                            index={index + 1}
                                            question={item}
                                            onAddAnswer={handleAddAnswer}
                                            onChangeType={handleChangeQuizType}
                                            onChangeQuestion={handleChangeQuestion}
                                            onChangeAnswer={handleChangeAnswers}
                                            onChecked={handleChangeChecked}
                                            onRemoveQuestion={handleRemoveQuestion}
                                        />
                                    )
                            )}
                            <div className="d-flex ssm-column jc-between ai-center mt-2">
                                <Pagination current={page} per={per} pers={[]} total={Math.ceil(questions.length / per)} onChange={(e) => setPage(e)} onPer={(e) => setPer(e)} />
                                <Button type="medium" onClick={handleAddQuestion}>
                                    <Icon name="fas fa-plus" size={14} color="#376caf" />
                                    <span className="dsl-p14 text-400 ms-1">ADD QUESTION</span>
                                </Button>
                            </div>
                        </>
                    )}

                    <Accordion className="settings" icon="">
                        <Row className="mt-1">
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field author"
                                    defaultIds={selected.author}
                                    title="Author"
                                    direction="vertical"
                                    width="100%"
                                    data={authors}
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('author')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field categories"
                                    defaultIds={data.data.category}
                                    title="Categories"
                                    direction="vertical"
                                    width="fit-content"
                                    multi
                                    data={categories}
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('category')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field competencies"
                                    defaultIds={data.data.competency}
                                    title="Competencies"
                                    direction="vertical"
                                    width="fit-content"
                                    multi
                                    data={competencies}
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('competency')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field department"
                                    defaultIds={data.data.department}
                                    title="Departments"
                                    direction="vertical"
                                    width="fit-content"
                                    multi
                                    data={departments}
                                    getValue={(e) => e.name}
                                    onChange={handleSelectTags('department')}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Dropdown
                                    className="dropdown-field completetime"
                                    title="Time to complete"
                                    direction="vertical"
                                    data={TimeIntervals}
                                    defaultIndexes={[0]}
                                    align="right"
                                    width="fit-content"
                                    getValue={(item) => item['label']}
                                />
                            </Col>
                            <Col xs={12} sm={6} className="my-3">
                                <Toggle
                                    className="toggle-button my-2"
                                    checked={approval}
                                    direction="vertical"
                                    title="Manager approval"
                                    leftLabel="Off"
                                    rightLabel="On"
                                    onChange={(e) => setApproval(e)}
                                />
                            </Col>
                        </Row>
                    </Accordion>

                    <div className="d-flex float-right mt-4">
                        {type.label !== 'Quiz' && <Button type="medium" name="Preview" onClick={handlePreview} />}
                        <Button className="ml-3" name="Draft" type="low" onClick={handleUpdate(0)} disabled={data?.published ? false : disabled} />
                        <Button className="ml-3" name="Publish" onClick={handleUpdate(1)} disabled={data?.published ? disabled : false} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Edit;
